import { faPen, faRefresh, faTrash } from "@fortawesome/free-solid-svg-icons";
import { graph } from "config/axios-context";
import { AppRoutes, resolveRoute } from "config/router";
import { DocumentNode } from "graphql";
import { ComponentStateTypes } from "../ComponentState";
import DeleteConfirmationModal from "../confirmation/DeleteConfirmationModal";
import ItemView, { ItemViewPropsType } from "../views/ItemView";
import Page, { PageActionsType, PagePropsType } from "./Page";
import CoreComponent from "utilities/component-utilities/CoreComponent";
import { useParams } from "react-router-dom";

const DELETE_MODAL_HASH = 'accommodation-event-datalist-delete-modal';

export type PageViewPropsType<TQuery> = Omit<PagePropsType, 'state' | 'children' | 'title'> & {
    deleteModelHash?: string,
    children?: React.ReactNode | undefined,
    editUrl?: string | AppRoutes,
    onDelete?: string | ((id: number) => Promise<void>),
    onDeleteResponse?: (() => void) | string,
    query: string | DocumentNode,
    queryDataPropertyName: keyof TQuery,
    name: string,
    view?: (item: TQuery) => React.ReactNode | ItemViewPropsType['model'],
    title: string | React.ReactNode | ((item?: TQuery) => string | React.ReactNode),
}

export type PageViewClassPropsType<TQuery> = Omit<PageViewPropsType<TQuery>, 'ref'> & {
    itemId: number,
}

type PageViewState<TQuery> = {
    componentState: ComponentStateTypes,
    model?: TQuery,
}

export type PageViewRef<TQuery> = PageViewClass<TQuery>

const DELETE_MUTATION = `mutation PageViewDelete($id: Int) {
    {onDelete}(id: $id)
}`;

class PageViewClass<TQuery> extends CoreComponent<PageViewClassPropsType<TQuery>, PageViewState<TQuery>> {
    constructor(props: PageViewClassPropsType<TQuery>) {
        super(props);
        this.state = {
            componentState: 'loading',
        }
    }

    componentDidMount() {
        this.loadData().then();
    }

    reload = async () => {
        await this.loadData();
    }

    data = () => {
        return this.state.model;
    }

    render() {
        const pageActions: PageActionsType = [
            {
                icon: faRefresh,
                link: () => this.loadData(),
                name: 'refresh',
                title: 'button.reload'
            },
            {
                icon: faPen,
                link: typeof this.props.editUrl === 'string' ? resolveRoute(this.props.editUrl || '', { id: this.props.itemId }) : '',
                name: 'edit',
                hide: this.props.editUrl === undefined,
                title: 'button.edit'
            },
            ...((this.props.actions as any) || []),
            {
                icon: faTrash,
                link: () => this.context.modal.open(this.props.deleteModelHash || DELETE_MODAL_HASH),
                name: 'delete',
                hide: this.props.onDelete === undefined,
                title: 'button.delete',
                className: 'text-danger'
            }
        ];

        return (
            <>
                <Page title={this.resolveTitle()} actions={pageActions} state={this.state.componentState} wFull={this.props.wFull}>
                    <div>{this.state.model ? this.resolveView() : ''}</div>
                    <div>{this.props.children}</div>
                </Page>
                <DeleteConfirmationModal
                    id={Number(this.props.itemId) || 0}
                    onConfirm={this.onDelete}
                    name={this.props.deleteModelHash || DELETE_MODAL_HASH}
                />
            </>
        );
    }

    loadData = async (soft?: boolean) => {
        if(!soft) {
            this.setState({ componentState: 'loading' });
        }
        try {
            const response = await graph<TQuery>({
                query: this.props.query,
                variables: {
                    id: Number(this.props.itemId) || 0
                }
            });

            if (response.errors) {
                throw response.errors;
            }

            const data = response.data;
            if (!data || !data[this.props.queryDataPropertyName]) {
                this.setState({
                    componentState: 'nodata',
                });
            } else {
                this.setState({
                    componentState: '',
                    model: data,
                })
            }
        } catch (error: any) {
            this.context.notify('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
            this.setState({
                componentState: 'error',
            });
            throw error;
        }
    }

    private resolveTitle = (): string | React.ReactNode => {
        if (typeof this.props.title === 'function') {
            return this.props.title(this.state.model);
        }

        return this.props.title;
    }

    private onDelete = async () => {
        if (typeof this.props.onDelete === 'string') {
            try {
                const response = await graph({
                    query: DELETE_MUTATION.replace('{onDelete}', this.props.onDelete),
                    variables: {
                        id: Number(this.props.itemId) || 0
                    }
                })

                if (response.errors) {
                    throw response.errors;
                }

                this.context.notify('success', 'request.delete.success');

                if (this.props.onDeleteResponse) {
                    if (typeof this.props.onDeleteResponse === 'function') {
                        this.props.onDeleteResponse();
                    } else {
                        this.context.navigate(this.props.onDeleteResponse, undefined, {
                            replace: true,
                        });
                    }
                } else {
                    this.context.navigate(-1);
                }
            } catch (error: any) {
                this.context.notify('error', 'request.delete.error', Array.isArray(error) ? error[0].message : error.message);
                throw error;
            }
        } else if (typeof this.props.onDelete == 'function') {
            await this.props.onDelete(this.props.itemId); if (this.props.onDeleteResponse) {
                if (typeof this.props.onDeleteResponse === 'function') {
                    this.props.onDeleteResponse();
                } else {
                    this.context.navigate(this.props.onDeleteResponse, undefined, {
                        replace: true,
                    });
                }
            } else {
                this.context.navigate(-1);
            }
        }
    }

    private resolveView = (): React.ReactNode => {
        if (!this.props.view) return <></>;
        const view = this.props.view(this.state.model!);
        if (Array.isArray(view)) {
            return <ItemView name={`${this.props.name}-view-properties`} model={view} view="default" />;
        }

        return view;
    }
}

const PageView = <TQuery,>(props: PageViewPropsType<TQuery>) => {
    const params = useParams<{ id: string }>();
    return <PageViewClass {...props} itemId={Number(params.id) || 0} />
}

export const PageViewForwardRef = <TQuery,>(props: PageViewPropsType<TQuery>, ref?: React.Ref<PageViewClass<TQuery>>) => {
    const params = useParams<{ id: string }>();
    return <PageViewClass {...props} itemId={Number(params.id) || 0} ref={ref} />;
}

// use by implementing forwardRef on the component
// const PageViewForwardRef = forwardRef<PageViewClass<TQuery>, PageViewPropsType<TQuery>>(PageViewForwardRef);

export default PageView;
